import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';

const { FiLayers, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiCopy } = FiIcons;

const BusinessLinesManagement = () => {
  const { data, addItem, updateItem, deleteItem, dataYear, dataAvailableYears, setDataYear, initializeBusinessLinesYear } = useAuth0Data();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    revenue: 0,
    goal: 0,
    margin: 0
  });

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...dataAvailableYears, currentYear, currentYear + 1])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    setDataYear(parseInt(e.target.value));
  };

  const handleInitializeYear = async () => {
    const previousYear = dataYear - 1;
    if (window.confirm(`Initialize ${dataYear} by copying business lines from ${previousYear}?\n\nThis will copy all business lines with their goals, but reset revenue to 0.`)) {
      setIsInitializing(true);
      try {
        await initializeBusinessLinesYear(dataYear);
      } catch (error) {
        alert(error.message);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  const handleAdd = () => {
    if (formData.name.trim()) {
      addItem('businessLines', { ...formData, year: dataYear });
      setFormData({ name: '', revenue: 0, goal: 0, margin: 0 });
      setIsAdding(false);
    }
  };

  const handleEdit = (line) => {
    setEditingId(line.id);
    setFormData({
      name: line.name,
      revenue: line.revenue,
      goal: line.goal,
      margin: line.margin
    });
  };

  const handleUpdate = () => {
    updateItem('businessLines', editingId, formData);
    setEditingId(null);
    setFormData({ name: '', revenue: 0, goal: 0, margin: 0 });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', revenue: 0, goal: 0, margin: 0 });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiLayers} className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="ml-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-900">Business Lines</h3>
              <select
                value={dataYear}
                onChange={handleYearChange}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium bg-white"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500">{data.businessLines.length} business lines for {dataYear}</p>
          </div>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Business Line
        </button>
      </div>

      {/* Add Form */}
      {isAdding && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-gray-50 rounded-lg"
        >
          <h4 className="font-semibold text-gray-900 mb-4">Add New Business Line</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Business line name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Revenue</label>
              <input
                type="number"
                value={formData.revenue}
                onChange={(e) => setFormData(prev => ({ ...prev, revenue: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
              <input
                type="number"
                value={formData.goal}
                onChange={(e) => setFormData(prev => ({ ...prev, goal: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Margin %</label>
              <input
                type="number"
                value={formData.margin}
                onChange={(e) => setFormData(prev => ({ ...prev, margin: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleAdd}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
              Save
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Business Lines List */}
      <div className="space-y-4">
        {data.businessLines.map((line, index) => (
          <motion.div
            key={line.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="border border-gray-200 rounded-lg p-4"
          >
            {editingId === line.id ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Revenue</label>
                    <input
                      type="number"
                      value={formData.revenue}
                      onChange={(e) => setFormData(prev => ({ ...prev, revenue: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
                    <input
                      type="number"
                      value={formData.goal}
                      onChange={(e) => setFormData(prev => ({ ...prev, goal: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Margin %</label>
                    <input
                      type="number"
                      value={formData.margin}
                      onChange={(e) => setFormData(prev => ({ ...prev, margin: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{line.name}</h4>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Revenue</p>
                    <p className="font-medium text-gray-900">{formatCurrency(line.revenue)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Goal</p>
                    <p className="font-medium text-gray-900">{formatCurrency(line.goal)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Margin</p>
                    <p className="font-medium text-gray-900">{line.margin}%</p>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(line)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteItem('businessLines', line.id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {data.businessLines.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <SafeIcon icon={FiLayers} className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="mb-4">No business lines found for {dataYear}.</p>
          {dataYear > Math.min(...dataAvailableYears) && (
            <button
              onClick={handleInitializeYear}
              disabled={isInitializing}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4 mr-2" />
              {isInitializing ? 'Initializing...' : `Initialize ${dataYear} from ${dataYear - 1}`}
            </button>
          )}
          <p className="text-sm mt-4">Or add a new business line to get started.</p>
        </div>
      )}
    </motion.div>
  );
};

export default BusinessLinesManagement;